<script setup lang="ts">
import { GetAllRepositoriesDocument } from '~/components/issues/AllRepositories.generated'
import { createSkillsRepositories, reinitializeRepositoryDiscovery } from '~/utils/issues/skillApi'

const modalOpen = defineModel<boolean>({ required: true })

const cachePlugin = useGqlCache()

interface SelectedRepository {
  gid: string
  owner: string
  name: string
  sourcePathPrefixIncludes: string
  showAdvanced: boolean
}

const selectedRepositories = ref<SelectedRepository[]>([])
const creating = ref(false)
const error = ref('')

const { data: repositoriesData, isFetching } = useQuery({
  query: GetAllRepositoriesDocument,
  tags: ['repositories'],
})

const repositoryOptions = computed(() => {
  if (!repositoriesData.value?.organization?.repositories?.edges) {
    return []
  }
  return repositoriesData.value.organization.repositories.edges
    .filter(edge => edge.node.gid)
    .map(edge => ({
      label: `${edge.node.owner}/${edge.node.name}`,
      value: edge.node.gid!,
      owner: edge.node.owner,
      name: edge.node.name,
    }))
})

const availableRepositories = computed(() => {
  const selectedGids = new Set(selectedRepositories.value.map(r => r.gid))
  return repositoryOptions.value.filter(r => !selectedGids.has(r.value))
})

const canCreate = computed(() => {
  return selectedRepositories.value.length > 0 && !creating.value
})

function addRepository(gid: string) {
  const repo = repositoryOptions.value.find(r => r.value === gid)
  if (repo && !selectedRepositories.value.find(r => r.gid === gid)) {
    selectedRepositories.value.push({
      gid: repo.value,
      owner: repo.owner,
      name: repo.name,
      sourcePathPrefixIncludes: '',
      showAdvanced: false,
    })
  }
}

function toggleAdvanced(gid: string) {
  const repo = selectedRepositories.value.find(r => r.gid === gid)
  if (repo) {
    repo.showAdvanced = !repo.showAdvanced
  }
}

function removeRepository(gid: string) {
  const index = selectedRepositories.value.findIndex(r => r.gid === gid)
  if (index !== -1) {
    selectedRepositories.value.splice(index, 1)
  }
}

async function createRepositories() {
  if (!canCreate.value) {
    return
  }

  creating.value = true
  error.value = ''

  try {
    const result = await createSkillsRepositories(
      selectedRepositories.value.map(r => ({
        repository_gid: r.gid,
        source_path_prefix_includes: r.sourcePathPrefixIncludes || undefined,
      })),
    )

    if (result.errors.length > 0 && result.created.length === 0) {
      error.value = result.errors.join(', ')
      return
    }

    if (result.created.length > 0) {
      const firstCreated = result.created[0]
      const repoGid = selectedRepositories.value.find(
        r => r.owner === firstCreated.repository_owner && r.name === firstCreated.repository_name,
      )?.gid

      // Start skill discovery BEFORE closing modal to ensure API call completes
      if (repoGid) {
        await reinitializeRepositoryDiscovery(firstCreated.gid)
      }

      // Clear cache and close modal
      cachePlugin.clearCache()
      closeModal()

      // Navigate using window.location.href for reliable navigation (like CreateSkillDialog)
      if (repoGid) {
        window.location.href = `/repositories/${repoGid}/suggested-skills?openChat=true`
      }
    }
  }
  catch (e: any) {
    error.value = e.message || 'Failed to create repositories'
  }
  finally {
    creating.value = false
  }
}

function closeModal() {
  modalOpen.value = false
  selectedRepositories.value = []
  error.value = ''
}
</script>

<template>
  <UModal v-model="modalOpen" :ui="{ width: 'w-full sm:max-w-xl' }">
    <ModalContentWrapper
      title="Add Skills Repository"
      subtitle="Select repositories to enable skills discovery"
      :scrollable-content="false"
      @close="closeModal"
    >
      <div class="flex flex-col gap-4">
        <UAlert
          v-if="error"
          color="red"
          variant="soft"
          :title="error"
          icon="i-heroicons-exclamation-triangle"
        />

        <UFormGroup label="Select Repository">
          <USelectMenu
            :options="availableRepositories"
            :loading="isFetching"
            searchable
            placeholder="Search and select a repository..."
            value-attribute="value"
            option-attribute="label"
            @update:model-value="addRepository"
          />
        </UFormGroup>

        <div v-if="selectedRepositories.length > 0" class="flex flex-col gap-3">
          <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
            Selected Repositories
          </div>
          <div
            v-for="repo in selectedRepositories"
            :key="repo.gid"
            class="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="font-medium text-gray-900 dark:text-white">
                {{ repo.owner }}/{{ repo.name }}
              </span>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600"
                  @click="toggleAdvanced(repo.gid)"
                >
                  <UIcon
                    name="solid:caret-right"
                    class="size-3 transition-transform duration-200"
                    :class="{ 'rotate-90': repo.showAdvanced }"
                  />
                  <span>advanced settings</span>
                </button>
                <UButton
                  icon="light:xmark"
                  variant="ghost"
                  color="gray"
                  size="xs"
                  @click="removeRepository(repo.gid)"
                />
              </div>
            </div>
            <div v-if="repo.showAdvanced" class="mt-2">
              <UFormGroup>
                <template #label>
                  <span class="text-xs text-gray-500">Path Includes (optional)</span>
                </template>
                <template #help>
                  <span class="text-xs text-gray-400">
                    Comma-separated paths for monorepo support, e.g., src/frontend,src/backend
                  </span>
                </template>
                <UInput
                  v-model="repo.sourcePathPrefixIncludes"
                  placeholder="e.g., src/frontend,src/backend"
                  size="sm"
                />
              </UFormGroup>
            </div>
          </div>
        </div>

        <div v-else class="py-4 text-center text-sm text-gray-500">
          Select one or more repositories to enable skills discovery
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <UButton variant="ghost" color="gray" @click="closeModal">
            Cancel
          </UButton>
          <UButton
            :disabled="!canCreate"
            :loading="creating"
            color="primary"
            @click="createRepositories"
          >
            Create
          </UButton>
        </div>
      </template>
    </ModalContentWrapper>
  </UModal>
</template>
